import {Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import Container = Phaser.GameObjects.Container;
import {SquareButton} from "@/scenes.ui/square-button";
import {Fonts} from "@/constants/font";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {StringUtils} from "@/utils/string-utils";
import {CreatureUtils} from "@/utils/creature-utils";
import {ForceUpdateInfo} from "@/store/force/types";
import {ForceBasePanel} from "@/scenes.ui/force-base-panel";
import PlayerStore from "@/store/player";
import {Lang} from "@/store/player/types";
import {WarriorClass} from "@/store/level/types";
import {CardType, EvolutionInfo} from "@/store/card/types";
import CardStore from "@/store/card";
import {CardPanel} from "@/scenes.ui/card-panel";
import {EvolutionPanel} from "@/scenes.ui/EvolutionPanel";
import {EvolutionGrid} from "@/scenes.ui/EvolutionGrid";
import {UpdateForcePanel} from "@/scenes.ui/update-force-panel";

export class ForceMaxLevelPanel extends ForceBasePanel {
    private ui: ForceUpdateInfo;
    private evolutionButton: SquareButton | null = null;
    private creatureId: number;
    private okButton: SquareButton | null = null;

    constructor(
        scene: Scene,
        canvas: HTMLCanvasElement,
        ui: ForceUpdateInfo,
        slotX: number,
        slotY: number,
        container: Container,
        creatureId: number
    ) {
        super(scene, canvas, slotX, slotY, container);
        this.ui = ui;
        this.creatureId = creatureId;

        let blackZone = SpriteUtils.addSprite(this.scene, 0, -150+2, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_BLACK_ZONE, 0.95, 1.7);
        let blackZone3 = SpriteUtils.addSprite(this.scene, 0, 50, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_BLACK_ZONE, 0.95, 3.60);
        blackZone3.setOrigin(0.5, 0);

        let hideRank = ui.currentLevel == 0 || (ui.currentLevel == 1 && !ui.canUpgrade);

        let forceName = PlayerStore.getPlayer().lang == Lang.RU ?
            StringUtils.capitalizeFirstLetter(Tr('force')) + ' ' + StringUtils.capitalizeFirstLetter(CreatureUtils.getClassNameGenetive(ui.warriorClass)) :
            StringUtils.capitalizeFirstLetter(CreatureUtils.getClassNameGenetive(ui.warriorClass)) + ' ' + StringUtils.capitalizeFirstLetter(Tr('force'));

        const title = TextUtils.addTextByFamily(this.scene, forceName, 0, -278, '#ffffff', 16,
            'normal', 420, Fonts.ARIAL_MT)
            .setOrigin(0.5, 0.5);

        let levelText = TextUtils.addTextByFamily(scene, hideRank? forceName : Tr('rang') + " " + String(ui.currentLevel) + ' (' + Tr('maks.dot') + ')',
            0, -223, '#ffedc6', 18, 'normal', 450, Fonts.MYRIADPRO_BOLD);

        let light = SpriteUtils.addSprite(this.scene, 0-4, -150 + 6, Keys.ATLAS_UI, Keys.UI_BADGE_LINER, 1.1);
        let item = SpriteUtils.addSprite(this.scene, 0, -150,
            CreatureUtils.getForceAtlasLarge(ui.warriorClass), CreatureUtils.getLargeForceIcon(ui.warriorClass, true), 0.8);

        const fromRankBg = SpriteUtils.addImage(this.scene,
            33, -150 +33, Keys.ATLAS_UI_FORCES, Keys.UI_FORCES_INDICATOR_BG, 1.1, 1.1).setOrigin(0.5, 0.5);
        let fromRankNumber = TextUtils.addTextByFamily(scene, ui.currentLevel.toString(),
            34, -150 + 35,'#daba6f', 15, 'bold', 450, Fonts.BENGALY_NORMAL).setOrigin(0.5, 0.5);
        fromRankBg.setVisible(!hideRank);
        fromRankNumber.setVisible(!hideRank);

        let forceInfoLabel = TextUtils.addTextAdvanced(scene, ui.description, 0, -22,
            '#d7d7d7', 13, 'normal', 300, 'center', 2, Fonts.ARIAL_MT);
        forceInfoLabel.setOrigin(0.5, 0.5);

        let maxLevelLabel = TextUtils.addTextAdvanced(scene, ui.currentLevel == 0? Tr('dar.poka.ne.dostupen') : Tr('sila.na.maksimalnom.urovne'), 0, 93,
            '#716f6a', 12, 'normal', 250, 'center', 2, Fonts.ARIAL_MT);

        this.okButton = new SquareButton(this.scene, 0, 192, 0,
            Keys.UI_ART_BROWN_BUTTON, Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('zakrit'), false);
        this.okButton.setScale(0.9, 0.9);
        this.okButton.setCallback(() => {
            if (this.isOnTop()) {
                this.close();
            }
        });

        this.add([blackZone, blackZone3, title, levelText, light, item, maxLevelLabel, fromRankBg, fromRankNumber,
            forceInfoLabel, this.okButton]);

        const activeCard = this.getActiveCard();
        const showTwoButtons = this.isTwoButtonMode(activeCard);

        if (showTwoButtons) {
            if (activeCard) {
                this.updateEvolutionButton();
            }
            let buttonsContainer = new Phaser.GameObjects.Container(this.scene, 0, 180);
            const spacing = 130 * CU.S;

            this.okButton.x = -spacing / 2;
            if (this.evolutionButton) {
                this.evolutionButton.x = spacing / 2;
                buttonsContainer.add(this.evolutionButton);
            }
            buttonsContainer.add(this.okButton);
            this.add(buttonsContainer);

            this.add(this.okButton);
            if (this.evolutionButton) this.add(this.evolutionButton);
        }
    }

    private getTargetClass(): WarriorClass {
        return this.ui.warriorClass;
    }

    private getActiveCard(): CardType | null {
        const cardState = PlayerStore.GET_CARDS_STATE();
        return cardState ? cardState.activeCard : null;
    }

    private isTwoButtonMode(activeCard: CardType | null): boolean {
        return !!activeCard
            && activeCard !== CardType.ELEPHANT
            && activeCard !== CardType.SNAKE
            && activeCard !== CardType.EAGLE;
    }

    private getCreatureId(): number {
        return this.creatureId;
    }

    private createEvolutionButton(activeCard: CardType): SquareButton {
        const cardIconKey = CardPanel.getCardSmallImage(activeCard);
        const btn = new SquareButton(
            this.scene, 60, 192, CU.S,
            Keys.UI_ART_GREEN_BUTTON, undefined, cardIconKey,
            Tr("evolution"), false, 90 * CU.S, 110 * CU.S
        );
        btn.setScale(0.9, 0.9);
        btn.setVisible(false);
        this.add(btn);
        return btn;
    }

    private updateEvolutionButton(): void {
        const activeCard = PlayerStore.GET_CARDS_STATE()?.activeCard;
        if (!activeCard) {
            return;
        }
        if (!this.evolutionButton) {
            this.evolutionButton = this.createEvolutionButton(activeCard);
        }
        const creatureId = this.getCreatureId();
        const currentForce = this.getTargetClass();

        const creature = CreatureUtils.getCreatureById(creatureId);
        if (!creature) {
            if (this.okButton) {
                this.okButton.x = 0;
            }
            this.evolutionButton?.setVisible(false);
            return;
        }

        const incompatibleError = UpdateForcePanel.isCardIncompatible(activeCard, creatureId);
        if (incompatibleError) {
            if (this.okButton) {
                this.okButton.x = 0;
                const warningY = this.okButton.displayHeight / CU.S + 240;
                const warningLabel = TextUtils.addTextByFamily(this.scene, incompatibleError, 0, warningY, "#ff4c4c", 13, "bold", 350, Fonts.ARIAL_MT).setOrigin(0.5, 0);
                this.okButton.y -= 10;
                this.okButton.setScale(0.7);
                this.add(warningLabel);
            }
            this.evolutionButton?.setVisible(false);
            return;
        }

        if (activeCard !== CardType.EVOLUTION_UNIVERSAL) {
            const targetWarriorClass = EvolutionPanel.getWarriorClassByCardType(activeCard);
            if (!targetWarriorClass) {
                return;
            }
            CardStore.checkEvolutionAvailability(creatureId, currentForce, targetWarriorClass)
                .then((canEvolve) => {
                    if (canEvolve) {
                        this.evolutionButton?.setVisible(true);
                        this.evolutionButton?.setCallback(() => {
                            new EvolutionPanel(
                                this.scene, this.scene.sys.canvas,
                                currentForce, targetWarriorClass, () => {
                                    this.setVisible(true);
                                }, () => {
                                }, this.x, this.y, this.container ?? new Container(this.scene), creatureId
                            ).open();
                            this.close();
                        });
                    } else {
                        if (this.okButton) {
                            this.okButton.x = 0;
                        }
                        this.evolutionButton?.setVisible(false);
                    }
                })
                .catch((error) => {
                    if (this.okButton) {
                        this.okButton.x = 0;
                    }
                    this.evolutionButton?.setVisible(false);
                });
        }
        else {
            const hasSelectedForce = CreatureUtils.hasForce(creature, this.getTargetClass());
            if (!hasSelectedForce) {
                if (this.okButton) {
                    this.okButton.x = 0;
                }
                this.evolutionButton?.setVisible(false);
                return;
            }
            this.evolutionButton?.setVisible(true);
            this.evolutionButton?.setCallback(() => {
                const heroName = creature.name || "hero";
                CardStore.fetchEvolutionOptionsByCreature(creatureId, currentForce).then((evolutionInfo: EvolutionInfo) => {
                    if (!evolutionInfo || !evolutionInfo.availableClasses || evolutionInfo.availableClasses.length === 0) {
                        return;
                    }
                    new EvolutionGrid(
                        this.scene, this.scene.sys.canvas,
                        evolutionInfo,
                        (selectedForce: WarriorClass) => {
                            new EvolutionPanel(
                                this.scene, this.scene.sys.canvas,
                                currentForce, selectedForce,
                                () => { this.setVisible(true); },
                                () => {},
                                this.x, this.y,
                                this.container ?? new Container(this.scene),
                                creatureId
                            ).open();
                            this.close();
                        },
                        this.getTargetClass(),
                        heroName
                    ).open();
                }).catch((error) => {
                    console.log(error);
                });
            });
        }
    }

    public open() {
        super.open();
        const activeCard = this.getActiveCard();
        if (this.isTwoButtonMode(activeCard)) {
            this.updateEvolutionButton();
        }
    }

    public close() {
        super.close();
    }
}